/**
 * Section Toggle Component
 * 
 * A custom element that provides tab-based navigation to toggle the visibility
 * of sections on the page. Sections can be targeted by ID or by a data-section-id attribute.
 */
if (!customElements.get('td-section-toggle')) {
  customElements.define('td-section-toggle', class extends HTMLElement {
    constructor() {
      super();
      
      // Component state
      this.tabs = null;
      this.tabMap = new Map();
      this.activeTab = null;
      this.transitionDuration = 300; // ms
    }

    connectedCallback() {
      this.setupComponent();
    }

    /**
     * Set up the component
     */
    setupComponent() {
      // Get tabs using this.querySelector to target elements inside the custom element
      this.tabs = this.querySelectorAll('.td-section-toggles__tab');
      if (!this.tabs.length) return;
      
      // Create a map of target IDs to tabs for easier lookup
      this.createTabMap();
      
      // Set up the initial state
      this.setupInitialState();
      
      // Attach event listeners
      this.attachEventListeners();
    }
    
    /**
     * Create a map of target IDs to tabs for easier lookup
     */
    createTabMap() {
      this.tabs.forEach(tab => {
        const targetId = tab.dataset.target;
        if (targetId) {
          this.tabMap.set(targetId, tab);
        }
      });
    }
    
    /**
     * Set up the initial state of the component
     */
    setupInitialState() {
      // Add fade transition styles to all target sections
      this.setupSectionStyles();
      
      // Hide all target sections initially
      this.hideAllTargetSections();
      
      // Determine the active tab based on URL hash or default to first tab
      this.determineActiveTab();
      
      // Activate the selected tab
      this.activateTab(this.activeTab);
    }
    
    /**
     * Determine the active tab based on URL hash or default to first tab
     */
    determineActiveTab() {
      // Default to first tab
      this.activeTab = this.tabs[0];
      
      // Check if there's a hash in the URL that matches a tab's target
      const hash = window.location.hash.substring(1);
      
      if (hash && this.tabMap.has(hash)) {
        // If hash matches a tab's target, use that tab
        this.activeTab = this.tabMap.get(hash);
      }
    }
    
    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Add click event listeners to tabs
      this.tabs.forEach(tab => {
        tab.addEventListener('click', this.handleTabClick.bind(this, tab));
      });
      
      // Listen for hash changes in the URL
      window.addEventListener('hashchange', this.handleHashChange.bind(this));
    }
    
    /**
     * Handle tab click event
     * @param {HTMLElement} tab - The clicked tab
     */
    handleTabClick(tab) {
      this.activateTab(tab);
      
      // Update URL hash when tab is clicked
      const targetId = tab.dataset.target;
      if (targetId) {
        window.history.pushState(null, '', `#${targetId}`);
      }
    }
    
    /**
     * Handle hash change event
     */
    handleHashChange() {
      const newHash = window.location.hash.substring(1);
      if (newHash && this.tabMap.has(newHash)) {
        this.activateTab(this.tabMap.get(newHash));
      }
    }
    
    /**
     * Set up transition styles for all target sections
     */
    setupSectionStyles() {
      this.tabs.forEach(tab => {
        const targetId = tab.dataset.target;
        if (!targetId) return;
        
        const section = this.findTargetSection(targetId);
        if (!section) return;
        
        // Add transition styles if not already present
        if (!section.classList.contains('td-section-toggle__target')) {
          section.classList.add('td-section-toggle__target');
          section.style.transition = `opacity ${this.transitionDuration / 1000}s ease`;
          section.style.opacity = '0';
          section.style.display = 'none';
        }
      });
    }
    
    /**
     * Activate a tab and show its target section
     * @param {HTMLElement} tab - The tab to activate
     */
    activateTab(tab) {
      if (!tab) return;
      
      // Remove active class from all tabs
      this.tabs.forEach(t => t.classList.remove('td-section-toggles__tab--active'));
      
      // Add active class to the selected tab
      tab.classList.add('td-section-toggles__tab--active');
      
      // Get the currently visible section, if any
      const visibleSection = this.getVisibleSection();
      
      // Get the target section for the selected tab
      const targetId = tab.dataset.target;
      if (!targetId) return;
      
      const sectionToShow = this.findTargetSection(targetId);
      if (!sectionToShow) return;
      
      // Handle section visibility changes
      this.handleSectionVisibilityChange(visibleSection, sectionToShow, targetId);
    }
    
    /**
     * Handle section visibility changes
     * @param {HTMLElement} visibleSection - Currently visible section
     * @param {HTMLElement} sectionToShow - Section to show
     * @param {string} targetId - Target ID
     */
    handleSectionVisibilityChange(visibleSection, sectionToShow, targetId) {
      // If there's a visible section and it's different from the target section
      if (visibleSection && visibleSection !== sectionToShow) {
        // Fade out the visible section
        this.fadeOut(visibleSection, () => {
          // After fade out completes, fade in the target section
          if (sectionToShow) {
            this.fadeIn(sectionToShow);
            this.dispatchChangeEvent(targetId, sectionToShow, visibleSection);
          }
        });
      } else if (sectionToShow) {
        // If there's no visible section, just fade in the target section
        this.fadeIn(sectionToShow);
        this.dispatchChangeEvent(targetId, sectionToShow, visibleSection);
      }
    }
    
    /**
     * Dispatch a custom event when a tab is changed
     * @param {string} targetId - Target ID
     * @param {HTMLElement} targetSection - Target section
     * @param {HTMLElement} previousSection - Previous section
     */
    dispatchChangeEvent(targetId, targetSection, previousSection) {
      window.dispatchEvent(new CustomEvent('td:section-switcher-changed', {
        detail: {
          targetId: targetId,
          targetSection: targetSection,
          previousSection: previousSection
        }
      }));
    }
    
    /**
     * Find the target section element
     * @param {string} targetId - Target ID
     * @returns {HTMLElement|null} - The target section element or null
     */
    findTargetSection(targetId) {
      // First try to find by data-section-id attribute
      const targetSectionWithId = document.querySelector(`[data-section-id="${targetId}"]`);
      if (targetSectionWithId) {
        // Get the parent section element
        const parentSection = targetSectionWithId.closest('section');
        if (parentSection) {
          return parentSection;
        }
      }
      
      // Fallback to ID if no data-section-id is found
      return document.getElementById(targetId);
    }
    
    /**
     * Get the currently visible section
     * @returns {HTMLElement|null} - The visible section or null
     */
    getVisibleSection() {
      for (const tab of this.tabs) {
        const targetId = tab.dataset.target;
        if (!targetId) continue;
        
        const section = this.findTargetSection(targetId);
        if (section && section.style.display !== 'none' && section.style.opacity !== '0') {
          return section;
        }
      }
      return null;
    }
    
    /**
     * Hide all target sections
     */
    hideAllTargetSections() {
      this.tabs.forEach(tab => {
        const targetId = tab.dataset.target;
        if (!targetId) return;
        
        const section = this.findTargetSection(targetId);
        if (section) {
          section.style.opacity = '0';
          section.style.display = 'none';
        }
      });
    }
    
    /**
     * Fade out a section
     * @param {HTMLElement} section - The section to fade out
     * @param {Function} callback - Callback function to call after fade out
     */
    fadeOut(section, callback) {
      if (!section) return;
      
      // Start fade out
      section.style.opacity = '0';
      
      // Wait for transition to complete
      const handleTransitionEnd = () => {
        section.style.display = 'none';
        section.removeEventListener('transitionend', handleTransitionEnd);
        if (typeof callback === 'function') {
          callback();
        }
      };
      
      section.addEventListener('transitionend', handleTransitionEnd);
      
      // Fallback in case the transitionend event doesn't fire
      setTimeout(() => {
        if (section.style.display !== 'none') {
          handleTransitionEnd();
        }
      }, this.transitionDuration + 50); // Slightly longer than the transition duration
    }
    
    /**
     * Fade in a section
     * @param {HTMLElement} section - The section to fade in
     */
    fadeIn(section) {
      if (!section) return;
      
      // Make sure the section is in the DOM but invisible
      section.style.display = 'block';
      section.style.opacity = '0';
      
      // Force a reflow to ensure the display change takes effect before starting the fade in
      // Using void section.offsetHeight to prevent optimization
      void section.offsetHeight;
      
      // Start fade in
      section.style.opacity = '1';
    }
    
    /**
     * Show a section by ID
     * @param {string} sectionId - The ID of the section to show
     */
    showSection(sectionId) {
      if (!sectionId) return;
      
      const section = this.findTargetSection(sectionId);
      if (section) {
        this.fadeIn(section);
      }
    }
  });
}
